import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Autocomplete,
  TextField,
  Alert,
  LinearProgress,
  Chip,
  Divider,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Cell,
  PieChart,
  Pie,
  Legend,
  ReferenceLine,
} from 'recharts';
import {
  TrendingUp,
  TrendingDown,
  Assessment,
  EmojiEvents,
  Sell,
  Close,
  EggAlt,
  AttachMoney,
  Payments,
  FoodBank,
  PieChart as PieChartIcon,
  MonetizationOn,
  Inventory2,
} from '@mui/icons-material';
import { useFinancialCycles } from '../hooks/useFinancialCycles';
import { useCyclePerformance, useProcurementBirdsBreakdown } from '../hooks/useCyclePerformance';
import type { ProcurementPerformance } from '../hooks/useCyclePerformance';

// ── formatters ────────────────────────────────────────────────────────────────

const fmt = (n: number) =>
  `GH₵ ${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtK = (v: number) => {
  const abs = Math.abs(v);
  if (abs >= 1_000_000) return `₵${(v / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `₵${(v / 1_000).toFixed(1)}k`;
  return `₵${v.toFixed(0)}`;
};

const fmtEggs = (v: number) => (v >= 1_000 ? `${(v / 1_000).toFixed(1)}k` : `${v}`);

// ── colour palettes ───────────────────────────────────────────────────────────

const RANK_COLORS = ['#ffd700', '#c0c0c0', '#cd7f32'];
const RANK_BG = ['rgba(255,215,0,0.12)', 'rgba(192,192,192,0.12)', 'rgba(205,127,50,0.12)'];
const RANK_BORDER = ['rgba(255,215,0,0.4)', 'rgba(192,192,192,0.35)', 'rgba(205,127,50,0.4)'];
const PIE_COLORS = [
  '#ff9800',
  '#42a5f5',
  '#66bb6a',
  '#ab47bc',
  '#26c6da',
  '#ef5350',
  '#26a69a',
  '#ffa726',
];

const AXIS_TICK = { fontSize: 11, fill: 'rgba(255,255,255,0.55)' } as const;
const GRID_STYLE = 'rgba(255,255,255,0.07)';

// ── StatCard ──────────────────────────────────────────────────────────────────

function StatCard({
  label,
  value,
  sub,
  color,
  icon,
}: {
  label: string;
  value: React.ReactNode;
  sub?: string;
  color: string;
  icon: React.ReactNode;
}) {
  return (
    <Paper
      sx={{
        p: 2,
        flex: '1 1 160px',
        minWidth: 150,
        borderLeft: `4px solid ${color}`,
        position: 'relative',
        overflow: 'hidden',
        boxShadow: `inset 0 1px 0 ${color}22`,
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          right: -10,
          top: -10,
          fontSize: 70,
          opacity: 0.05,
          color,
          lineHeight: 1,
          pointerEvents: 'none',
          display: 'flex',
        }}
      >
        {icon}
      </Box>
      <Typography
        variant="caption"
        sx={{
          color: 'text.secondary',
          fontWeight: 600,
          letterSpacing: 0.8,
          textTransform: 'uppercase',
          fontSize: '0.62rem',
          display: 'block',
        }}
      >
        {label}
      </Typography>
      <Typography variant="h5" fontWeight={800} sx={{ color, mt: 0.5, lineHeight: 1.2 }}>
        {value}
      </Typography>
      {sub && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25, display: 'block' }}>
          {sub}
        </Typography>
      )}
    </Paper>
  );
}

// ── Chart tooltips ────────────────────────────────────────────────────────────

const ProfitTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  const v: number = payload[0].value;
  return (
    <Paper sx={{ p: 1.5, border: '1px solid rgba(255,255,255,0.1)', minWidth: 170 }}>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={700} color={v >= 0 ? '#4caf50' : '#f44336'}>
        {v >= 0 ? '+' : ''}
        {fmt(v)}
      </Typography>
    </Paper>
  );
};

const RevExpTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <Paper sx={{ p: 1.5, border: '1px solid rgba(255,255,255,0.1)', minWidth: 180 }}>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
        {label}
      </Typography>
      {payload.map((entry: any) => (
        <Box
          key={entry.name}
          sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 0.25 }}
        >
          <Typography variant="caption" sx={{ color: entry.color }}>
            {entry.name}
          </Typography>
          <Typography variant="caption" fontWeight={700} sx={{ color: entry.color }}>
            {fmt(entry.value)}
          </Typography>
        </Box>
      ))}
    </Paper>
  );
};

const PieTooltip = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null;
  const entry = payload[0];
  return (
    <Paper sx={{ p: 1.5, border: '1px solid rgba(255,255,255,0.1)' }}>
      <Typography variant="caption" color="text.secondary" display="block">
        {entry.name}
      </Typography>
      <Typography variant="body2" fontWeight={700} sx={{ color: entry.payload.fill ?? '#ff9800' }}>
        {fmtEggs(entry.value)} eggs
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {entry.payload.sharePct?.toFixed(1)}% of production
      </Typography>
    </Paper>
  );
};

// ── BreakdownRow (detail dialog) ──────────────────────────────────────────────

const BreakdownRow: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
  color?: string;
  bold?: boolean;
}> = ({ icon, label, value, sub, color, bold }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      py: 1,
      borderBottom: '1px solid rgba(255,255,255,0.05)',
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Box sx={{ color: color ?? 'rgba(255,255,255,0.4)', display: 'flex' }}>{icon}</Box>
      <Box>
        <Typography
          variant="body2"
          fontWeight={bold ? 700 : 500}
          color={bold ? '#fff' : 'rgba(255,255,255,0.75)'}
        >
          {label}
        </Typography>
        {sub && (
          <Typography variant="caption" color="rgba(255,255,255,0.35)">
            {sub}
          </Typography>
        )}
      </Box>
    </Box>
    <Typography variant="body2" fontWeight={bold ? 800 : 600} color={color ?? '#fff'}>
      {value}
    </Typography>
  </Box>
);

// ── Page ──────────────────────────────────────────────────────────────────────

const CyclePerformancePage: React.FC = () => {
  const [selectedCycleId, setSelectedCycleId] = useState<number | null>(null);
  const [detailBatch, setDetailBatch] = useState<ProcurementPerformance | null>(null);

  const { data: birdBreakdowns, isFetching: birdsLoading } = useProcurementBirdsBreakdown(
    selectedCycleId,
    detailBatch?.procurement_id ?? null
  );
  const { data: cyclesData, isFetching: cyclesLoading } = useFinancialCycles();
  const { data: performanceData, isFetching } = useCyclePerformance(selectedCycleId);

  const cycles = cyclesData || [];
  const procurements: ProcurementPerformance[] = performanceData?.procurements ?? [];
  const sorted = [...procurements].sort((a, b) => b.financials.profit - a.financials.profit);
  const summary = performanceData?.summary;
  const topPerformer = sorted[0] ?? null;

  const sellCandidates = sorted.filter(
    (p) => p.financials.profit < 0 && p.egg_production.total_eggs > 0
  );
  const worstSellCandidate = sellCandidates[sellCandidates.length - 1] ?? null;

  const positiveTotal = sorted
    .filter((p) => p.financials.profit > 0)
    .reduce((s, p) => s + p.financials.profit, 0);
  const profitImpactPct = (p: ProcurementPerformance) =>
    positiveTotal > 0 && p.financials.profit > 0 ? (p.financials.profit / positiveTotal) * 100 : 0;
  const maxProfit = sorted[0]?.financials.profit ?? 1;

  // ── chart data ──────────────────────────────────────────────────────────────
  const profitChartData = sorted.map((p) => ({
    name: p.identifier,
    profit: p.financials.profit,
  }));

  const revExpChartData = sorted.map((p) => ({
    name: p.identifier,
    Revenue: p.financials.total_sales,
    Expenses: p.financials.total_expenditures,
  }));

  const costStackData = sorted.map((p) => ({
    name: p.identifier,
    Direct: p.financials.direct_expenditures,
    Salary: p.financials.salary_expenditures ?? 0,
    Overhead: p.financials.allocated_expenditures,
  }));

  const prodShareData = sorted.map((p, i) => ({
    name: p.identifier,
    value: p.egg_production.total_eggs,
    sharePct: p.egg_production.production_share_pct,
    fill: PIE_COLORS[i % PIE_COLORS.length],
  }));

  const profitChartHeight = Math.max(220, sorted.length * 52);

  return (
    <Box sx={{ p: 3 }}>
      {(cyclesLoading || isFetching) && (
        <LinearProgress
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
            height: 3,
            '& .MuiLinearProgress-bar': {
              background: 'linear-gradient(90deg, #ff9800, #ffa726)',
            },
          }}
        />
      )}

      {/* ── Page header ── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3,
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              background: 'linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(76,175,80,0.4)',
            }}
          >
            <Assessment sx={{ color: '#fff', fontSize: 22 }} />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight={700}>
              Cycle Performance
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Profitability by bird procurement batch
            </Typography>
          </Box>
        </Box>

        <Autocomplete
          options={cycles}
          getOptionLabel={(option: any) =>
            `${option.name} (${new Date(option.start_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}${option.end_date ? ' – ' + new Date(option.end_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : ' – Active'})`
          }
          value={cycles.find((c: any) => c.id === selectedCycleId) || null}
          onChange={(_, v: any) => setSelectedCycleId(v?.id || null)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Financial Cycle"
              placeholder="Select a cycle…"
              size="small"
            />
          )}
          sx={{ width: 340 }}
        />
      </Box>

      {!selectedCycleId && (
        <Paper sx={{ p: 6, textAlign: 'center', border: '1px solid rgba(255,152,0,0.15)' }}>
          <Assessment sx={{ fontSize: 52, color: 'rgba(255,255,255,0.15)', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Select a financial cycle to begin
          </Typography>
          <Typography color="text.secondary">
            Choose a cycle above to see profitability metrics broken down per batch
          </Typography>
        </Paper>
      )}

      {summary && (
        <>
          {/* ── Summary strip ── */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            <StatCard
              label="Batches"
              value={summary.total_procurements}
              color="#66bb6a"
              icon={<Inventory2 sx={{ fontSize: 'inherit' }} />}
            />
            <StatCard
              label="Total Eggs"
              value={fmtEggs(summary.total_eggs)}
              sub={`${(summary.total_eggs / 30).toFixed(0)} crates`}
              color="#42a5f5"
              icon={<EggAlt sx={{ fontSize: 'inherit' }} />}
            />
            <StatCard
              label="Revenue"
              value={fmtK(summary.total_sales)}
              sub={fmt(summary.total_sales)}
              color="#ff9800"
              icon={<AttachMoney sx={{ fontSize: 'inherit' }} />}
            />
            <StatCard
              label="Total Expenses"
              value={fmtK(summary.total_expenditures)}
              sub={fmt(summary.total_expenditures)}
              color="#ef5350"
              icon={<Payments sx={{ fontSize: 'inherit' }} />}
            />
            <StatCard
              label={summary.total_profit >= 0 ? 'Net Profit' : 'Net Loss'}
              value={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {summary.total_profit >= 0 ? (
                    <TrendingUp sx={{ fontSize: 22 }} />
                  ) : (
                    <TrendingDown sx={{ fontSize: 22 }} />
                  )}
                  {fmtK(Math.abs(summary.total_profit))}
                </Box>
              }
              sub={`${summary.overall_profit_margin.toFixed(1)}% margin`}
              color={summary.total_profit >= 0 ? '#4caf50' : '#f44336'}
              icon={<MonetizationOn sx={{ fontSize: 'inherit' }} />}
            />
          </Box>

          {summary.unallocated_expenditures > 0 && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              <Typography variant="body2" fontWeight={600}>
                Unallocated overhead: {fmt(summary.unallocated_expenditures)}
              </Typography>
              <Typography variant="caption">
                Shared across {summary.total_procurements} batch(es) weighted by bird count
              </Typography>
            </Alert>
          )}

          {/* ── Intelligence panel ── */}
          {sorted.length >= 1 && (
            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
              {topPerformer && (
                <Paper
                  sx={{
                    flex: '1 1 280px',
                    p: 2.5,
                    background:
                      'linear-gradient(135deg, rgba(255,215,0,0.1) 0%, rgba(255,152,0,0.05) 100%)',
                    border: '2px solid rgba(255,215,0,0.45)',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <EmojiEvents sx={{ color: '#ffd700', fontSize: 24 }} />
                    <Typography variant="subtitle2" fontWeight={700} color="#ffd700">
                      Top Performing Batch
                    </Typography>
                  </Box>
                  <Typography variant="h6" fontWeight={800}>
                    {topPerformer.identifier}
                  </Typography>
                  {topPerformer.bird_colors && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ mb: 1.5 }}
                    >
                      {topPerformer.bird_colors} · Procured{' '}
                      {new Date(topPerformer.date_procured).toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </Typography>
                  )}
                  <Box sx={{ display: 'flex', gap: 3, mb: 1.5, flexWrap: 'wrap' }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Profit
                      </Typography>
                      <Typography variant="h6" fontWeight={800} color="#4caf50">
                        {fmt(topPerformer.financials.profit)}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Margin
                      </Typography>
                      <Typography variant="h6" fontWeight={800} color="#66bb6a">
                        {topPerformer.financials.profit_margin.toFixed(1)}%
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Eggs
                      </Typography>
                      <Typography variant="h6" fontWeight={800} color="#42a5f5">
                        {topPerformer.egg_production.total_eggs.toLocaleString()}
                      </Typography>
                    </Box>
                  </Box>
                  {positiveTotal > 0 && (
                    <>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          Profit contribution
                        </Typography>
                        <Typography variant="caption" fontWeight={700} color="#ffd700">
                          {profitImpactPct(topPerformer).toFixed(1)}% of total earnings
                        </Typography>
                      </Box>
                      <Box sx={{ height: 6, bgcolor: 'rgba(255,255,255,0.08)', borderRadius: 3 }}>
                        <Box
                          sx={{
                            height: '100%',
                            width: `${Math.min(profitImpactPct(topPerformer), 100)}%`,
                            bgcolor: '#ffd700',
                            borderRadius: 3,
                          }}
                        />
                      </Box>
                    </>
                  )}
                </Paper>
              )}

              {worstSellCandidate && (
                <Paper
                  sx={{
                    flex: '1 1 280px',
                    p: 2.5,
                    background:
                      'linear-gradient(135deg, rgba(244,67,54,0.12) 0%, rgba(244,67,54,0.05) 100%)',
                    border: '2px solid rgba(244,67,54,0.5)',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <Sell sx={{ color: '#f44336', fontSize: 22 }} />
                    <Typography variant="subtitle2" fontWeight={700} color="#f44336">
                      Losing Money — Consider Selling
                    </Typography>
                  </Box>
                  <Typography variant="h6" fontWeight={800}>
                    {worstSellCandidate.identifier}
                  </Typography>
                  {worstSellCandidate.bird_colors && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ mb: 0.5 }}
                    >
                      {worstSellCandidate.bird_colors} · Procured{' '}
                      {new Date(worstSellCandidate.date_procured).toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </Typography>
                  )}
                  <Typography
                    variant="caption"
                    sx={{ display: 'block', mb: 1.5, color: '#ef9a9a', fontStyle: 'italic' }}
                  >
                    Still laying but expenses exceed revenue
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 3, mb: 1.5, flexWrap: 'wrap' }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Loss
                      </Typography>
                      <Typography variant="h6" fontWeight={800} color="#f44336">
                        -{fmt(Math.abs(worstSellCandidate.financials.profit))}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Margin
                      </Typography>
                      <Typography variant="h6" fontWeight={800} color="#f44336">
                        {worstSellCandidate.financials.profit_margin.toFixed(1)}%
                      </Typography>
                    </Box>
                  </Box>
                  <Alert
                    severity="error"
                    sx={{ py: 0.5, '& .MuiAlert-message': { fontSize: '0.75rem' } }}
                  >
                    Decommissioning stops a loss of{' '}
                    {fmt(Math.abs(worstSellCandidate.financials.profit))} this cycle
                    {sellCandidates.length > 1 &&
                      ` · ${sellCandidates.length - 1} other batch${sellCandidates.length > 2 ? 'es' : ''} also losing`}
                  </Alert>
                </Paper>
              )}
            </Box>
          )}

          {/* ── Charts ── */}
          {sorted.length > 0 && (
            <>
              <Divider sx={{ borderColor: 'rgba(255,152,0,0.15)', mb: 3 }} />

              {/* Row 1: Profit bar + Production donut */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', lg: '1.6fr 1fr' },
                  gap: 2,
                  mb: 2,
                }}
              >
                {/* Profit / Loss by batch */}
                <Paper sx={{ p: 2.5 }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                    Profit / Loss by Batch
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ mb: 2 }}
                  >
                    Sorted highest to lowest — click a card below for full breakdown
                  </Typography>
                  <ResponsiveContainer width="100%" height={profitChartHeight}>
                    <BarChart
                      layout="vertical"
                      data={profitChartData}
                      margin={{ left: 8, right: 24, top: 4, bottom: 4 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke={GRID_STYLE} horizontal={false} />
                      <XAxis
                        type="number"
                        tickFormatter={fmtK}
                        tick={AXIS_TICK}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis
                        type="category"
                        dataKey="name"
                        width={80}
                        tick={AXIS_TICK}
                        axisLine={false}
                        tickLine={false}
                      />
                      <ReferenceLine x={0} stroke="rgba(255,255,255,0.25)" strokeWidth={1} />
                      <RechartsTooltip
                        content={(props: any) => <ProfitTooltip {...props} />}
                        cursor={{ fill: 'rgba(255,255,255,0.04)' }}
                      />
                      <Bar dataKey="profit" radius={[0, 4, 4, 0]} maxBarSize={32}>
                        {profitChartData.map((entry, i) => (
                          <Cell
                            key={i}
                            fill={entry.profit >= 0 ? '#4caf50' : '#f44336'}
                            fillOpacity={0.85}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Paper>

                {/* Egg production share */}
                <Paper sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <PieChartIcon sx={{ fontSize: 16, color: '#ff9800' }} />
                    <Typography variant="subtitle2" fontWeight={700}>
                      Egg Production Share
                    </Typography>
                  </Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ mb: 1.5 }}
                  >
                    % of total eggs each batch produced
                  </Typography>
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie
                        data={prodShareData}
                        cx="50%"
                        cy="45%"
                        innerRadius={58}
                        outerRadius={85}
                        dataKey="value"
                        paddingAngle={2}
                      >
                        {prodShareData.map((entry, i) => (
                          <Cell key={i} fill={entry.fill} />
                        ))}
                      </Pie>
                      <RechartsTooltip content={(props: any) => <PieTooltip {...props} />} />
                      <Legend
                        formatter={(value: string) => (
                          <Typography component="span" variant="caption" color="text.secondary">
                            {value}
                          </Typography>
                        )}
                        iconSize={8}
                        iconType="circle"
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </Paper>
              </Box>

              {/* Row 2: Revenue vs Expenses + Cost breakdown */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
                  gap: 2,
                  mb: 3,
                }}
              >
                {/* Revenue vs Expenses */}
                <Paper sx={{ p: 2.5 }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                    Revenue vs Expenses
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ mb: 2 }}
                  >
                    Revenue bar above expenses = profitable
                  </Typography>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart
                      data={revExpChartData}
                      margin={{ left: 4, right: 4, top: 4, bottom: 4 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke={GRID_STYLE} vertical={false} />
                      <XAxis dataKey="name" tick={AXIS_TICK} axisLine={false} tickLine={false} />
                      <YAxis
                        tickFormatter={fmtK}
                        tick={AXIS_TICK}
                        axisLine={false}
                        tickLine={false}
                        width={56}
                      />
                      <RechartsTooltip
                        content={(props: any) => <RevExpTooltip {...props} />}
                        cursor={{ fill: 'rgba(255,255,255,0.04)' }}
                      />
                      <Bar
                        dataKey="Revenue"
                        fill="#4caf50"
                        fillOpacity={0.8}
                        radius={[4, 4, 0, 0]}
                        maxBarSize={36}
                      />
                      <Bar
                        dataKey="Expenses"
                        fill="#f44336"
                        fillOpacity={0.8}
                        radius={[4, 4, 0, 0]}
                        maxBarSize={36}
                      />
                      <Legend
                        formatter={(value: string) => (
                          <Typography component="span" variant="caption" color="text.secondary">
                            {value}
                          </Typography>
                        )}
                        iconSize={8}
                        iconType="circle"
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Paper>

                {/* Cost breakdown stacked */}
                <Paper sx={{ p: 2.5 }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                    Expense Breakdown
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ mb: 2 }}
                  >
                    Direct costs, salary, and overhead per batch
                  </Typography>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart
                      data={costStackData}
                      margin={{ left: 4, right: 4, top: 4, bottom: 4 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke={GRID_STYLE} vertical={false} />
                      <XAxis dataKey="name" tick={AXIS_TICK} axisLine={false} tickLine={false} />
                      <YAxis
                        tickFormatter={fmtK}
                        tick={AXIS_TICK}
                        axisLine={false}
                        tickLine={false}
                        width={56}
                      />
                      <RechartsTooltip
                        content={(props: any) => <RevExpTooltip {...props} />}
                        cursor={{ fill: 'rgba(255,255,255,0.04)' }}
                      />
                      <Bar
                        dataKey="Direct"
                        stackId="a"
                        fill="#ef5350"
                        fillOpacity={0.85}
                        maxBarSize={36}
                      />
                      <Bar
                        dataKey="Salary"
                        stackId="a"
                        fill="#ffa726"
                        fillOpacity={0.85}
                        maxBarSize={36}
                      />
                      <Bar
                        dataKey="Overhead"
                        stackId="a"
                        fill="rgba(255,255,255,0.35)"
                        fillOpacity={0.9}
                        radius={[4, 4, 0, 0]}
                        maxBarSize={36}
                      />
                      <Legend
                        formatter={(value: string) => (
                          <Typography component="span" variant="caption" color="text.secondary">
                            {value}
                          </Typography>
                        )}
                        iconSize={8}
                        iconType="circle"
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Paper>
              </Box>
            </>
          )}

          {/* ── Ranked batch list ── */}
          {sorted.length > 0 && (
            <>
              <Divider sx={{ borderColor: 'rgba(255,152,0,0.15)', mb: 3 }} />
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 2,
                }}
              >
                <Typography variant="subtitle1" fontWeight={700} color="text.secondary">
                  All Batches — Ranked by Profit
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Click any row for the full cost breakdown
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {sorted.map((p, idx) => {
                  const rankColor = RANK_COLORS[idx] ?? 'rgba(255,255,255,0.35)';
                  const rankBg = RANK_BG[idx] ?? 'rgba(255,255,255,0.04)';
                  const rankBorder = RANK_BORDER[idx] ?? 'rgba(255,255,255,0.15)';
                  const isTop = idx === 0;
                  const isLosing = p.financials.profit < 0 && p.egg_production.total_eggs > 0;
                  const contribution = profitImpactPct(p);
                  const profitBarWidth =
                    maxProfit > 0 ? Math.max(0, (p.financials.profit / maxProfit) * 100) : 0;
                  const shareBarWidth = p.egg_production.production_share_pct ?? 0;

                  return (
                    <Paper
                      key={p.procurement_id}
                      onClick={() => setDetailBatch(p)}
                      sx={{
                        p: 2,
                        background: `linear-gradient(135deg, ${rankBg} 0%, transparent 100%)`,
                        border: `1px solid ${rankBorder}`,
                        cursor: 'pointer',
                        transition: 'all 0.15s',
                        '&:hover': {
                          borderColor: rankColor,
                          boxShadow: `0 0 0 1px ${rankColor}30, 0 4px 16px rgba(0,0,0,0.3)`,
                          transform: 'translateY(-1px)',
                        },
                      }}
                    >
                      <Box
                        sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}
                      >
                        {/* Rank badge */}
                        <Box
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: '50%',
                            bgcolor: rankBg,
                            border: `2px solid ${rankColor}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          <Typography fontWeight={900} fontSize={17} color={rankColor}>
                            #{idx + 1}
                          </Typography>
                        </Box>

                        {/* Identity + bars */}
                        <Box sx={{ flex: 1, minWidth: 180 }}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              flexWrap: 'wrap',
                              mb: 0.25,
                            }}
                          >
                            <Typography variant="h6" fontWeight={800}>
                              {p.identifier}
                            </Typography>
                            {isTop && (
                              <Chip
                                label="Top Performer"
                                size="small"
                                icon={
                                  <EmojiEvents
                                    sx={{
                                      fontSize: '13px !important',
                                      color: '#ffd700 !important',
                                    }}
                                  />
                                }
                                sx={{
                                  bgcolor: 'rgba(255,215,0,0.15)',
                                  color: '#ffd700',
                                  fontWeight: 700,
                                  border: '1px solid rgba(255,215,0,0.4)',
                                  fontSize: '0.7rem',
                                }}
                              />
                            )}
                            {isLosing && (
                              <Chip
                                label="Losing Money"
                                size="small"
                                sx={{
                                  bgcolor: 'rgba(244,67,54,0.15)',
                                  color: '#f44336',
                                  fontWeight: 700,
                                  border: '1px solid rgba(244,67,54,0.4)',
                                  fontSize: '0.7rem',
                                }}
                              />
                            )}
                          </Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            sx={{ mb: 1.5 }}
                          >
                            {p.bird_colors ?? '—'} · Procured{' '}
                            {new Date(p.date_procured).toLocaleDateString('en-GB', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric',
                            })}
                          </Typography>

                          {/* Egg share bar */}
                          <Box sx={{ mb: 1 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.4 }}>
                              <Typography variant="caption" color="text.secondary">
                                Egg share
                              </Typography>
                              <Typography variant="caption" fontWeight={700} color={rankColor}>
                                {shareBarWidth.toFixed(1)}%
                              </Typography>
                            </Box>
                            <Box
                              sx={{ height: 4, bgcolor: 'rgba(255,255,255,0.07)', borderRadius: 2 }}
                            >
                              <Box
                                sx={{
                                  height: '100%',
                                  width: `${shareBarWidth}%`,
                                  bgcolor: rankColor,
                                  borderRadius: 2,
                                }}
                              />
                            </Box>
                          </Box>

                          {/* Profit bar */}
                          <Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.4 }}>
                              <Typography variant="caption" color="text.secondary">
                                Profit vs top performer
                              </Typography>
                              {contribution > 0 && (
                                <Typography variant="caption" color="text.secondary">
                                  {contribution.toFixed(1)}% of total profit
                                </Typography>
                              )}
                            </Box>
                            <Box
                              sx={{ height: 4, bgcolor: 'rgba(255,255,255,0.07)', borderRadius: 2 }}
                            >
                              <Box
                                sx={{
                                  height: '100%',
                                  width: `${profitBarWidth}%`,
                                  bgcolor: p.financials.profit >= 0 ? '#4caf50' : '#f44336',
                                  borderRadius: 2,
                                }}
                              />
                            </Box>
                          </Box>
                        </Box>

                        {/* Metrics */}
                        <Box
                          sx={{
                            display: 'flex',
                            gap: 3,
                            flexWrap: 'wrap',
                            alignItems: 'flex-start',
                          }}
                        >
                          <Box sx={{ minWidth: 80 }}>
                            <Typography variant="caption" color="text.secondary" display="block">
                              Eggs
                            </Typography>
                            <Typography variant="body2" fontWeight={700} color="#42a5f5">
                              {p.egg_production.total_eggs.toLocaleString()}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {p.egg_production.total_crates.toFixed(1)} crates
                            </Typography>
                          </Box>
                          <Box sx={{ minWidth: 100 }}>
                            <Typography variant="caption" color="text.secondary" display="block">
                              Revenue
                            </Typography>
                            <Typography variant="body2" fontWeight={700} color="#66bb6a">
                              {fmt(p.financials.total_sales)}
                            </Typography>
                          </Box>
                          <Box sx={{ minWidth: 100 }}>
                            <Typography variant="caption" color="text.secondary" display="block">
                              Expenses
                            </Typography>
                            <Typography variant="body2" fontWeight={700} color="#ef5350">
                              {fmt(p.financials.total_expenditures)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Direct: {fmt(p.financials.direct_expenditures)}
                            </Typography>
                          </Box>
                          <Box sx={{ minWidth: 110 }}>
                            <Typography variant="caption" color="text.secondary" display="block">
                              P / L
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              {p.financials.profit >= 0 ? (
                                <TrendingUp sx={{ fontSize: 16, color: '#4caf50' }} />
                              ) : (
                                <TrendingDown sx={{ fontSize: 16, color: '#f44336' }} />
                              )}
                              <Typography
                                variant="body2"
                                fontWeight={800}
                                color={p.financials.profit >= 0 ? '#4caf50' : '#f44336'}
                              >
                                {p.financials.profit < 0 ? '-' : ''}
                                {fmt(Math.abs(p.financials.profit))}
                              </Typography>
                            </Box>
                            <Chip
                              label={`${p.financials.profit_margin.toFixed(1)}% margin`}
                              size="small"
                              sx={{
                                mt: 0.5,
                                bgcolor:
                                  p.financials.profit >= 0
                                    ? 'rgba(76,175,80,0.15)'
                                    : 'rgba(244,67,54,0.15)',
                                color: p.financials.profit >= 0 ? '#66bb6a' : '#f44336',
                                fontSize: '0.68rem',
                                fontWeight: 600,
                                height: 20,
                              }}
                            />
                          </Box>
                        </Box>
                      </Box>
                    </Paper>
                  );
                })}
              </Box>
            </>
          )}
        </>
      )}

      {/* ── Batch detail dialog ── */}
      <Dialog
        open={!!detailBatch}
        onClose={() => setDetailBatch(null)}
        maxWidth="sm"
        fullWidth
        slotProps={{
          paper: { sx: { bgcolor: '#1a1a1a', border: '1px solid rgba(255,152,0,0.2)' } },
        }}
      >
        {detailBatch && (
          <>
            {/* Dialog header */}
            <Box
              sx={{
                px: 2.5,
                pt: 2,
                pb: 1.5,
                background: 'linear-gradient(135deg, rgba(255,152,0,0.12) 0%, transparent 100%)',
                borderBottom: '1px solid rgba(255,152,0,0.15)',
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
              }}
            >
              <Box>
                <Typography variant="h6" fontWeight={800}>
                  {detailBatch.identifier}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {detailBatch.bird_colors ?? '—'} · Procured{' '}
                  {new Date(detailBatch.date_procured).toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                  })}
                </Typography>
              </Box>
              <IconButton onClick={() => setDetailBatch(null)} size="small" sx={{ mt: -0.5 }}>
                <Close fontSize="small" />
              </IconButton>
            </Box>

            {/* P&L mini-visual */}
            <Box
              sx={{
                px: 2.5,
                py: 1.5,
                bgcolor: 'rgba(255,255,255,0.02)',
                borderBottom: '1px solid rgba(255,255,255,0.06)',
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{
                  mb: 1,
                  fontWeight: 600,
                  letterSpacing: 0.5,
                  textTransform: 'uppercase',
                  fontSize: '0.62rem',
                }}
              >
                Revenue vs Expenses
              </Typography>
              {(() => {
                const rev = detailBatch.financials.total_sales;
                const exp = detailBatch.financials.total_expenditures;
                const maxVal = Math.max(rev, exp);
                return (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                        <Typography variant="caption" color="#66bb6a" fontWeight={600}>
                          Revenue
                        </Typography>
                        <Typography variant="caption" color="#66bb6a" fontWeight={700}>
                          {fmt(rev)}
                        </Typography>
                      </Box>
                      <Box sx={{ height: 8, bgcolor: 'rgba(255,255,255,0.06)', borderRadius: 2 }}>
                        <Box
                          sx={{
                            height: '100%',
                            width: `${(rev / maxVal) * 100}%`,
                            bgcolor: '#4caf50',
                            borderRadius: 2,
                          }}
                        />
                      </Box>
                    </Box>
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                        <Typography variant="caption" color="#ef5350" fontWeight={600}>
                          Expenses
                        </Typography>
                        <Typography variant="caption" color="#ef5350" fontWeight={700}>
                          {fmt(exp)}
                        </Typography>
                      </Box>
                      <Box sx={{ height: 8, bgcolor: 'rgba(255,255,255,0.06)', borderRadius: 2 }}>
                        <Box
                          sx={{
                            height: '100%',
                            width: `${(exp / maxVal) * 100}%`,
                            bgcolor: '#f44336',
                            borderRadius: 2,
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>
                );
              })()}
            </Box>

            <DialogContent dividers sx={{ borderColor: 'rgba(255,152,0,0.12)' }}>
              <Stack spacing={2.5}>
                {/* Revenue section */}
                <Box>
                  <Typography
                    variant="overline"
                    color="text.secondary"
                    sx={{ letterSpacing: 1.5, fontSize: '0.65rem' }}
                  >
                    Revenue
                  </Typography>
                  <BreakdownRow
                    icon={<EggAlt fontSize="small" />}
                    label="Egg production"
                    value={`${detailBatch.egg_production.total_eggs.toLocaleString()} eggs`}
                    sub={`${detailBatch.egg_production.total_crates.toFixed(1)} crates · ${detailBatch.egg_production.production_share_pct.toFixed(1)}% of farm output`}
                    color="#42a5f5"
                  />
                  <BreakdownRow
                    icon={<AttachMoney fontSize="small" />}
                    label="Prorated sales revenue"
                    value={fmt(detailBatch.financials.total_sales)}
                    sub={`${detailBatch.egg_production.production_share_pct.toFixed(1)}% share of cycle sales`}
                    color="#66bb6a"
                    bold
                  />
                </Box>

                <Divider sx={{ borderColor: 'rgba(255,255,255,0.07)' }} />

                {/* Expenses section */}
                <Box>
                  <Typography
                    variant="overline"
                    color="text.secondary"
                    sx={{ letterSpacing: 1.5, fontSize: '0.65rem' }}
                  >
                    Expenses
                  </Typography>
                  <BreakdownRow
                    icon={<FoodBank fontSize="small" />}
                    label="Direct costs"
                    value={fmt(detailBatch.financials.direct_expenditures)}
                    sub="Feed (batch assignments) + vet & bird-specific expenses"
                    color="#ef5350"
                  />
                  <BreakdownRow
                    icon={<Payments fontSize="small" />}
                    label="Employee salaries"
                    value={fmt(detailBatch.financials.salary_expenditures ?? 0)}
                    sub="Allocated by batch assignment"
                    color="#ffa726"
                  />
                  <BreakdownRow
                    icon={<PieChartIcon fontSize="small" />}
                    label="Allocated overhead"
                    value={fmt(detailBatch.financials.allocated_expenditures)}
                    sub="Farm-wide overhead weighted by bird count"
                    color="rgba(255,255,255,0.5)"
                  />
                  <BreakdownRow
                    icon={<TrendingDown fontSize="small" />}
                    label="Total expenses"
                    value={fmt(detailBatch.financials.total_expenditures)}
                    color="#ef5350"
                    bold
                  />
                </Box>

                <Divider sx={{ borderColor: 'rgba(255,255,255,0.07)' }} />

                {/* Net */}
                <Box>
                  <Typography
                    variant="overline"
                    color="text.secondary"
                    sx={{ letterSpacing: 1.5, fontSize: '0.65rem' }}
                  >
                    Net
                  </Typography>
                  <BreakdownRow
                    icon={
                      detailBatch.financials.profit >= 0 ? (
                        <TrendingUp fontSize="small" />
                      ) : (
                        <TrendingDown fontSize="small" />
                      )
                    }
                    label={detailBatch.financials.profit >= 0 ? 'Net Profit' : 'Net Loss'}
                    value={`${detailBatch.financials.profit < 0 ? '-' : ''}${fmt(Math.abs(detailBatch.financials.profit))}`}
                    sub={`${detailBatch.financials.profit_margin.toFixed(1)}% margin`}
                    color={detailBatch.financials.profit >= 0 ? '#4caf50' : '#f44336'}
                    bold
                  />
                </Box>

                {/* Per-bird breakdown */}
                {(birdBreakdowns?.length ?? 0) > 0 && (
                  <>
                    <Divider sx={{ borderColor: 'rgba(255,255,255,0.07)' }} />
                    <Box>
                      <Typography
                        variant="overline"
                        color="text.secondary"
                        sx={{ letterSpacing: 1.5, fontSize: '0.65rem' }}
                      >
                        Per Bird Group
                      </Typography>

                      {birdsLoading && (
                        <LinearProgress
                          sx={{
                            mt: 1,
                            borderRadius: 1,
                            '& .MuiLinearProgress-bar': { background: '#ff9800' },
                          }}
                        />
                      )}

                      {birdBreakdowns?.map((bird) => (
                        <Box
                          key={bird.bird_id}
                          sx={{
                            mt: 1.5,
                            p: 1.5,
                            borderRadius: 2,
                            border: '1px solid rgba(255,255,255,0.08)',
                            bgcolor: 'rgba(255,255,255,0.02)',
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              mb: 1,
                            }}
                          >
                            <Box>
                              <Typography variant="body2" fontWeight={700}>
                                {bird.identifier}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {bird.current_quantity.toLocaleString()} alive of{' '}
                                {bird.quantity_bought.toLocaleString()} bought ·{' '}
                                {bird.egg_production.total_eggs.toLocaleString()} eggs (
                                {bird.egg_production.production_share_pct.toFixed(1)}%)
                              </Typography>
                            </Box>
                            <Chip
                              label={
                                bird.financials.profit >= 0
                                  ? `+${fmt(bird.financials.profit)}`
                                  : `-${fmt(Math.abs(bird.financials.profit))}`
                              }
                              size="small"
                              sx={{
                                bgcolor:
                                  bird.financials.profit >= 0
                                    ? 'rgba(76,175,80,0.15)'
                                    : 'rgba(244,67,54,0.15)',
                                color: bird.financials.profit >= 0 ? '#4caf50' : '#f44336',
                                fontWeight: 700,
                                border: `1px solid ${bird.financials.profit >= 0 ? 'rgba(76,175,80,0.3)' : 'rgba(244,67,54,0.3)'}`,
                                fontSize: '0.7rem',
                              }}
                            />
                          </Box>
                          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5 }}>
                            {[
                              {
                                label: 'Revenue',
                                value: bird.financials.revenue,
                                color: '#66bb6a',
                              },
                              {
                                label: 'Direct costs',
                                value: bird.financials.direct_cost,
                                color: '#ef5350',
                              },
                              {
                                label: 'Salary share',
                                value: bird.financials.salary_cost,
                                color: '#ffa726',
                              },
                              {
                                label: 'Overhead share',
                                value: bird.financials.overhead_cost,
                                color: 'rgba(255,255,255,0.5)',
                              },
                              {
                                label: 'Margin',
                                value: null,
                                display: `${bird.financials.profit_margin.toFixed(1)}%`,
                                color: bird.financials.profit >= 0 ? '#4caf50' : '#f44336',
                              },
                            ].map((row) => (
                              <Box
                                key={row.label}
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  px: 1,
                                  py: 0.25,
                                  borderRadius: 1,
                                  bgcolor: 'rgba(255,255,255,0.02)',
                                }}
                              >
                                <Typography variant="caption" color="text.secondary">
                                  {row.label}
                                </Typography>
                                <Typography variant="caption" fontWeight={600} color={row.color}>
                                  {row.display ?? fmt(row.value ?? 0)}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </>
                )}
              </Stack>
            </DialogContent>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default CyclePerformancePage;
